/**
 * Esquemas de **Flyers de precios** (Planes → Flyers, pedido de Viva de octubre 2026).
 *
 * Viva armaba a mano en Canva los flyers mensuales de precios y cada aumento los dejaba
 * desfasados del sistema. Acá el flyer guarda **sólo textos y elecciones** (qué plantilla,
 * qué planes, qué disciplinas, qué lista): **ningún importe se tipea** (RN-CAJ-04). Los
 * números los calcula el backend contra la lista de precios versionada en
 * `GET /api/flyers/:id/render-data` y llegan con la forma de `FlyerRenderData`.
 */
import { z } from "zod";
import { objectId, pagination } from "./base.js";
/** Las tres plantillas que reproducen los flyers que Viva ya publica. */
export const FLYER_TEMPLATES = ["plan_table", "special_classes", "text_promo"];
/** Logos que puede llevar el pie del flyer (Viva + plataformas de las que es socio). */
export const FLYER_LOGOS = ["viva", "sportclub", "wellhub"];
export const FLYER_STATUSES = ["draft", "published"];
/**
 * Archivos que el club puede subir para reemplazar los de fábrica: la tipografía real de
 * Canva por rol (brush / condensada / texto) y el logo oficial de cada plataforma.
 */
export const FLYER_ASSET_SLOTS = [
    "font_brush",
    "font_condensed",
    "font_text",
    "logo_sportclub",
    "logo_wellhub",
];
/** Tipos de archivo aceptados por slot: fuentes woff2/ttf, logos PNG/SVG. */
export const FLYER_FONT_CONTENT_TYPES = ["font/woff2", "font/ttf"];
export const FLYER_LOGO_CONTENT_TYPES = ["image/png", "image/svg+xml"];
export const FLYER_ASSET_MAX_BYTES = 2 * 1024 * 1024;
export function isFontSlot(slot) {
    return slot.startsWith("font_");
}
/**
 * Texto libre por fila: `benefit:<planId>` pisa la columna BENEFICIOS; `note:<planId>` es
 * la leyenda extra de la fila («Ahorrás hasta $62.000»). Las claves se validan con regex:
 * así ni un `$` ni un `.` llegan a una clave de Mongo.
 */
export const flyerFreeTextKey = z
    .string()
    .regex(/^(benefit|note):[0-9a-fA-F]{24}$/, "Clave de texto libre inválida");
const flyerText = (max) => z.string().trim().max(max);
/** Los campos del flyer sin defaults: la base de alta, edición parcial y vista previa. */
const flyerFields = {
    template: z.enum(FLYER_TEMPLATES),
    /** Nombre interno, para encontrarlo en la lista. No sale en el flyer. */
    title: z.string().trim().min(2).max(120),
    /** «OCTUBRE»: va en mayúscula con contorno blanco. */
    monthLabel: flyerText(30),
    /** «SE VIENE CON TODO!», «¡PROMO REFERIDOS!». */
    headline: flyerText(80),
    /** Bajada del titular (texto de la promo, nombre de la familia de planes). */
    subheadline: flyerText(240),
    /** Filas de la tabla (y base de la cifra «desde $X por mes»). El orden es el del flyer. */
    planIds: z.array(objectId).max(6),
    /** Plan mensual del bloque DÉBITO AUTOMÁTICO / VALOR PLAN MENSUAL + MATRÍCULA. */
    monthlyPlanId: objectId.nullable(),
    showDebit: z.boolean(),
    showMonthly: z.boolean(),
    /** Disciplinas de la plantilla de clases especiales. */
    disciplineIds: z.array(objectId).max(12),
    /** Bullets con flecha naranja. */
    bullets: z.array(flyerText(160).min(1)).max(8),
    /** Letra chica al pie. */
    footnotes: z.array(flyerText(200).min(1)).max(6),
    /** Pisa la cifra destacada calculada («desde $63.000 por mes»). `null` = la calculada. */
    highlightText: flyerText(80).nullable(),
    freeTexts: z.record(flyerFreeTextKey, flyerText(80)),
    logos: z.array(z.enum(FLYER_LOGOS)).max(FLYER_LOGOS.length),
    /** Lista de precios elegida. `null` = la vigente al momento de mirar el flyer. */
    priceListId: objectId.nullable(),
};
export const createFlyerBody = z.object({
    template: flyerFields.template,
    title: flyerFields.title,
    monthLabel: flyerFields.monthLabel.default(""),
    headline: flyerFields.headline.default(""),
    subheadline: flyerFields.subheadline.default(""),
    planIds: flyerFields.planIds.default([]),
    monthlyPlanId: flyerFields.monthlyPlanId.default(null),
    showDebit: flyerFields.showDebit.default(false),
    showMonthly: flyerFields.showMonthly.default(false),
    disciplineIds: flyerFields.disciplineIds.default([]),
    bullets: flyerFields.bullets.default([]),
    footnotes: flyerFields.footnotes.default([]),
    highlightText: flyerFields.highlightText.default(null),
    freeTexts: flyerFields.freeTexts.default({}),
    logos: flyerFields.logos.default(["viva"]),
    priceListId: flyerFields.priceListId.default(null),
});
/** Edición parcial: sin defaults, para que un PATCH no pise lo que no manda. */
export const updateFlyerBody = z
    .object({ ...flyerFields, status: z.enum(FLYER_STATUSES) })
    .partial();
/**
 * Vista previa del editor: la configuración sin guardar, con los mismos defaults del alta.
 * El nombre interno no hace falta para calcular.
 */
export const flyerPreviewBody = createFlyerBody.omit({ title: true });
export const listFlyersQuery = pagination.extend({
    status: z.enum(FLYER_STATUSES).optional(),
    template: z.enum(FLYER_TEMPLATES).optional(),
});
export const flyerAssetSlotParams = z.object({ slot: z.enum(FLYER_ASSET_SLOTS) });
export const flyerAssetUploadQuery = z.object({
    fileName: z.string().trim().min(1).max(160),
});
//# sourceMappingURL=flyers.js.map